/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

export const PROGRESS_UPDATE_MODAL_LAYOUT_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_LAYOUT;
  background: transparent !important;
  z-index: 3000 !important;

  > div {
    display: flex !important;
    flex-direction: column !important;
    grid-template-columns: minmax(0, 1fr) !important;
    width: 100% !important;
    max-width: 520px !important;
    max-height: calc(100vh - 48px) !important;
    border-radius: 0 !important;
    border: 1px solid ${COLORS.ink} !important;
    background: ${COLORS.white} !important;
    overflow: hidden !important;
  }
`;

export const PROGRESS_UPDATE_MODAL_PANEL_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_PANEL;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
  position: relative;
`;

export const PROGRESS_UPDATE_MODAL_CLOSE_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_CLOSE;
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  padding: 6px;
  background: none;
  border: 0;
  cursor: pointer;
  line-height: 0;

  svg {
    width: 10px;
    height: 10px;
    stroke: ${COLORS.midtone};
    stroke-width: 1.6;
    fill: none;
    display: block;
  }

  &:hover svg { stroke: ${COLORS.ink}; }
`;

export const PROGRESS_UPDATE_MODAL_HEAD_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_HEAD;
  flex-shrink: 0;
  padding: 24px 28px 0;
`;

export const PROGRESS_UPDATE_MODAL_EYEBROW_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_EYEBROW;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.ink};
`;

export const PROGRESS_UPDATE_MODAL_TITLE_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_TITLE;
  margin: 16px 0 0;
  padding-right: 28px;
  font-family: ${FONT.display};
  font-size: ${pxToRem(22)};
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: ${COLORS.ink};
`;

export const PROGRESS_UPDATE_MODAL_SUBTITLE_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_SUBTITLE;
  margin: 8px 0 0;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

export const PROGRESS_UPDATE_MODAL_BODY_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_BODY;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 28px 0;
`;

export const PROGRESS_UPDATE_MODAL_SECTION_LABEL_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_SECTION_LABEL;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
  margin: 18px 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:first-of-type { margin-top: 0; }
`;

export const PROGRESS_UPDATE_MODAL_NOTE_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_NOTE;
  width: 100%;
  box-sizing: border-box;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  color: ${COLORS.ink};
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  padding: 10px;
  outline: none;
  resize: vertical;
  min-height: 72px;

  &:focus {
    border-color: ${COLORS.ink};
    box-shadow: inset 0 0 0 1px ${COLORS.ink};
  }
`;

export const PROGRESS_UPDATE_MODAL_NOTE_COUNT_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_NOTE_COUNT;
  text-align: right;
  margin-top: 4px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${COLORS.midtone};
`;

export const PROGRESS_UPDATE_MODAL_NOTE_HINT_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_NOTE_HINT;
  margin-top: 4px;
  font-size: ${pxToRem(12)};
  color: ${COLORS.midtone};
`;

export const PROGRESS_UPDATE_MODAL_NOTE_COUNT_LIMIT_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_NOTE_COUNT_LIMIT;
  color: ${COLORS.error};
`;

export const PROGRESS_UPDATE_MODAL_FOOTER_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_FOOTER;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 28px 24px;
  margin-top: 10px;
`;

export const PROGRESS_UPDATE_MODAL_BUTTON_ROW_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_BUTTON_ROW;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
`;

export const PROGRESS_UPDATE_MODAL_BUTTON_BASE_STYLE = css`
  label: PROGRESS_UPDATE_MODAL_BUTTON_BASE;
  flex-shrink: 0;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(11)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 13px 22px;
  cursor: pointer;
  border: 1px solid ${COLORS.ink};
  background: ${COLORS.white};
  color: ${COLORS.ink};

  &:disabled {
    background: ${COLORS.stroke};
    border-color: ${COLORS.stroke};
    color: ${COLORS.midtone};
    cursor: not-allowed;
  }
`;

export const PROGRESS_UPDATE_MODAL_GHOST_BUTTON_STYLE = css`
  ${PROGRESS_UPDATE_MODAL_BUTTON_BASE_STYLE};
  label: PROGRESS_UPDATE_MODAL_GHOST_BUTTON;
  border-color: ${COLORS.stroke};
  color: ${COLORS.midtone};

  &:hover:not(:disabled) {
    border-color: ${COLORS.ink};
    color: ${COLORS.ink};
  }
`;

export const PROGRESS_UPDATE_MODAL_PRIMARY_BUTTON_STYLE = css`
  ${PROGRESS_UPDATE_MODAL_BUTTON_BASE_STYLE};
  label: PROGRESS_UPDATE_MODAL_PRIMARY_BUTTON;
  background: ${COLORS.ink};
  border-color: ${COLORS.ink};
  color: ${COLORS.white};

  &:hover:not(:disabled) { filter: brightness(1.25); }
`;

export const PROGRESS_UPDATE_STATUS_GRID_STYLE = css`
  label: PROGRESS_UPDATE_STATUS_GRID;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const PROGRESS_UPDATE_STATUS_OPTION_STYLE = css`
  label: PROGRESS_UPDATE_STATUS_OPTION;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 10px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  cursor: pointer;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13)};
  font-weight: 600;
  color: ${COLORS.midtone};
  transition: border-color 0.12s, color 0.12s, background 0.12s;

  &:hover {
    border-color: ${COLORS.midtone};
    color: ${COLORS.ink};
  }
`;

export const PROGRESS_UPDATE_STATUS_OPTION_SELECTED_STYLE = css`
  label: PROGRESS_UPDATE_STATUS_OPTION_SELECTED;
  border-color: currentColor;
`;

export const PROGRESS_UPDATE_STATUS_CURRENT_DOT_STYLE = css`
  label: PROGRESS_UPDATE_STATUS_CURRENT_DOT;
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.45;
`;

export const PROGRESS_UPDATE_DROPZONE_WRAP_STYLE = css`
  label: PROGRESS_UPDATE_DROPZONE_WRAP;
  margin-top: 0;
`;

export const PROGRESS_UPDATE_FILE_LIST_STYLE = css`
  label: PROGRESS_UPDATE_FILE_LIST;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

export const PROGRESS_UPDATE_FILE_ROW_STYLE = css`
  label: PROGRESS_UPDATE_FILE_ROW;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

export const PROGRESS_UPDATE_FILE_NAME_STYLE = css`
  label: PROGRESS_UPDATE_FILE_NAME;
  flex: 1;
  min-width: 0;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  color: ${COLORS.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PROGRESS_UPDATE_FILE_REMOVE_STYLE = css`
  label: PROGRESS_UPDATE_FILE_REMOVE;
  flex-shrink: 0;
  background: none;
  border: 0;
  padding: 4px;
  cursor: pointer;
  line-height: 0;

  svg {
    width: 13px;
    height: 13px;
    stroke: ${COLORS.midtone};
    stroke-width: 1.7;
    fill: none;
  }

  &:hover svg { stroke: ${COLORS.error}; }
`;
